import { useChildMutations } from "../api/useChildMutations";
import { refetchCurrentLead } from "../api/useCurrentLead";
import { useLeadMutations } from "../api/useLeadMutations";
import {
  useChildDeadlines,
  useChildFollowUps,
  useChildForm,
  useChildNotes,
} from "../contexts/ChildProvider"; // Adjust path
import {
  useLeadDeadlines,
  useLeadFollowUps,
  useLeadForm,
  useLeadNotes,
} from "../contexts/FormLeadContext";

export const useActiveDataMutation = (): any => {
  // 1. Grab both contexts
  const leadContext = useLeadForm();
  const childContext = useChildForm();

  // 2. Grab Lead Form Data (We use 'watch' to get real-time keystrokes)
  const leadFormData = leadContext.watch(); // Watches all fields
  const childFormData = childContext.watch();
  const leadFolowUps = leadFormData.followUps;
  const childFolowUps = childFormData.followUps;
  const leadDeadlines = leadFormData.deadlines;
  const childDeadlines = childFormData.deadlines;

  // 3. Determine Mode
  const isChildActive = !!childContext.currentChildId;
  const activeChild = childContext.child; // The API data for the child
  const activeData = isChildActive ? activeChild : leadContext?.lead;
  const activeRegister = isChildActive
    ? leadContext.register
    : childContext.register;

  const {
    addNote: childAddNote,
    notes: childNotes,
    updateNote: childUpdateNote,
  } = useChildNotes();
  const {
    addNote: leadAddNote,
    notes: leadNotes,
    updateNote: leadUpdateNote,
  } = useLeadNotes();
  const { updateStage: leadUpdateStage } = useLeadMutations();
  const { updateStage: childUpdateStage } = useChildMutations();

  const { addFollowUp: leadAddFollowUp } = useLeadFollowUps();

  const { addFollowUp: childAddFollowUp } = useChildFollowUps();

  const {
    addDeadline: leadAddDeadline,
    completeDeadline: leadCompleteDeadline,
  } = useLeadDeadlines();

  const {
    addDeadline: childAddDeadline,
    completeDeadline: childCompleteDeadline,
  } = useChildDeadlines();

  const addNote = isChildActive ? childAddNote : leadAddNote;
  const addFollowUp = isChildActive ? childAddFollowUp : leadAddFollowUp;
  const addDeadline = isChildActive ? childAddDeadline : leadAddDeadline;

  const completeDeadlineSwitcher = (
    scope: "child" | "lead",
    index: number,
    completedUser: { id: string | number; name: string }
  ) => {
    if (scope === "child") {
      childCompleteDeadline(index, completedUser);
    } else {
      leadCompleteDeadline(index, completedUser);
    }
  };

  // Tag each note with its source so edit/delete can route back to the
  // right RHF field array (the merged+sorted list loses the origin index).
  const notes = [
    ...(leadNotes ?? []).map((n: any) => ({ ...n, noteScope: "lead" })),
    ...(childNotes ?? []).map((n: any) => ({ ...n, noteScope: "child" })),
  ].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  /**
   * Patch one note's local form state after a successful server edit/delete.
   * `field` is an entry from the merged `notes` list (carries the RHF key in
   * `id` + `noteScope`); both are stripped before writing the value back.
   */
  const applyNoteUpdate = (field: any, patch: Record<string, unknown>) => {
    const isChildNote = field.noteScope === "child";
    const list: any[] = (isChildNote ? childNotes : leadNotes) ?? [];
    const index = list.findIndex((f: any) => f.id === field.id);
    if (index < 0) return;
    const { id: _rhfKey, noteScope: _scope, ...value } = field;
    const next = { ...value, ...patch };
    if (isChildNote) childUpdateNote(index, next);
    else leadUpdateNote(index, next);
  };
  const followUps = [...(leadFolowUps ?? []), ...(childFolowUps ?? [])].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  const deadlines = [...(leadDeadlines ?? []), ...(childDeadlines ?? [])].sort(
    (a, b) =>
      new Date(b.date ?? b.createdAt).getTime() -
      new Date(a.date ?? a.createdAt).getTime()
  );

  // Attachments are ALWAYS lead-scoped (the backend collapses every upload to
  // ("leads", leadId)). The child detail API deliberately surfaces the SAME
  // lead documents on `child.documents`, so a naive merge listed every file
  // twice whenever a deal/child was open. Dedupe by id (falling back to a
  // stable file key) so each document appears exactly once.
  const mergedDocuments = [
    ...(leadFormData?.documents ?? []),
    ...(childFormData?.documents ?? []),
  ];
  const seenDocKeys = new Set<string>();
  const documents = mergedDocuments.filter((doc: any) => {
    const key = String(
      doc?.id ?? doc?.s3Key ?? doc?.fileUrl ?? doc?.fileName ?? ""
    );
    if (key === "") return true; // no stable key — keep rather than drop
    if (seenDocKeys.has(key)) return false;
    seenDocKeys.add(key);
    return true;
  });

  // apis
  const updateStage = isChildActive ? childUpdateStage : leadUpdateStage;

  return {
    mergedData: leadFormData,
    lead: leadContext?.lead,
    refetchLead: refetchCurrentLead,
    activeData: activeData,
    leadFolowUps,
    childFolowUps,
    leadDeadlines,
    childDeadlines,
    child: activeChild,
    isChildActive,
    leadRegister: leadContext.register,
    leadControl: leadContext.control,
    addNote,
    applyNoteUpdate,
    addFollowUp,
    // Grain-explicit variants — the slot picker writes a LEAD-assigned
    // role's follow-up to the LEAD even while a deal is active (the slot
    // lives where the role is assigned).
    leadAddFollowUp,
    childAddFollowUp,
    addDeadline,
    completeDeadlineSwitcher,
    notes,
    followUps,
    deadlines,
    documents,
    handleChildChange: childContext.handleChildChange,
    activeRegister,
    // api's
    updateStage,
  };
};
